/** Vehicle API types and helpers */
import { getApiUrl, API_BASE_URL } from "@/lib/api-config";
import { getAuthToken } from "@/lib/auth-token";

export function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface VehicleRateOption {
  id: number;
  city_id: number;
  booking_type: string;
  km_limit?: number | null;
  hour_limit?: number | null;
  extra_time_charge?: string | null;
  price: number;
  starting_price: string;
  extra_km_charge: string;
}

export interface ChauffeurPackage {
  id: number;
  vehicle_variant_id: number;
  booking_type: string;
  trip_type: "one_way" | "round_trip";
  km_limit: number;
  price: number;
  starting_price: string;
  extra_km_charge: string;
  extra_time_charge?: string | null;
}

export interface Vehicle {
  id: number;
  vehicle_variant_id?: number;
  vehicle_model_id?: number;
  vehicle_rate_id?: number;
  name: string;
  brand?: string;
  model?: string;
  model_name?: string;
  variant_name?: string;
  slug: string;
  vehicle_type: string;
  body_type: string;
  fuel_type: string;
  transmission: string;
  seating_capacity: number;
  segment?: string;
  /** Price for city + booking_type (from vehicle_rates) */
  starting_price: string;
  price?: number;
  km_limit?: number | null;
  hour_limit?: number | null;
  extra_km_charge?: string;
  extra_time_charge?: string | null;
  image_url: string;
  rates?: VehicleRateOption[];
  chauffeur_packages?: ChauffeurPackage[];
  specifications?: VehicleSpec[];
  images?: VehicleImage[];
}

export interface VehicleSpec {
  key: string;
  value: string;
}

export interface VehicleImage {
  id: number;
  image_url: string;
  image_type?: string;
  is_primary?: boolean;
}

/** Vehicle detail - no pricing (pricing is on listing page) */
export interface VehicleDetail extends Omit<Vehicle, "starting_price"> {
  brand?: string;
  model?: string;
  about?: string;
  segment?: string;
  variant_name?: string;
  vehicle_variant_id?: number;
  vehicle_model_id?: number;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  specifications?: VehicleSpec[];
  images?: VehicleImage[];
}

export interface VehiclesResponse {
  success: boolean;
  message: string;
  data: Vehicle[];
}

export interface CityOption {
  id: number;
  name: string;
}

export interface LocationOption {
  id: number;
  city_id: number;
  name: string;
  address: string;
  pincode: string;
  distance?: number | string | null;
  charge?: number | string | null;
}

/** Convert ISO date string to API format: YYYY-MM-DD HH:mm:ss */
export function toApiDateTime(iso: string): string {
  if (!iso) return "";
  const normalized = iso.includes(" ") ? iso.replace(" ", "T") : iso;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
}

/** Build full image URL */
export function vehicleImageUrl(path: string | null | undefined): string {
  if (!path) return "";
  let cleanPath = path;
  if (path.includes("localhost") || path.includes("127.0.0.1")) {
    const storageIndex = path.indexOf("/storage/");
    if (storageIndex !== -1) {
      cleanPath = path.substring(storageIndex);
    }
  }
  let resolvedUrl = cleanPath.startsWith("http")
    ? cleanPath
    : `${API_BASE_URL}${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    resolvedUrl = resolvedUrl.replace(/^http:/, "https:");
  }
  return resolvedUrl;
}

/** Map spec key to display label */
export function specKeyToLabel(key: string): string {
  const map: Record<string, string> = {
    fuel_type: "Fuel Type",
    transmission: "Transmission",
    seating_capacity: "Seats",
    body_type: "Body Type",
    segment: "Segment",
    engine: "Engine",
    engine_cc: "Engine CC",
    mileage: "Mileage",
    bootspace: "Boot Space",
    boot_space: "Boot Space",
    luggage: "Luggage",
    ground_clearance: "Ground Clearance",
    ac: "Air Conditioning",
    air_conditioning: "Air Conditioning",
    bluetooth: "Bluetooth Audio",
    features: "Features & Amenities",
    safety: "Safety Systems",
    airbags: "Airbags",
  };
  return map[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Fetch single vehicle by slug. Requires backend: GET /api/v1/web/vehicles/{slug} */
export async function fetchVehicleBySlug(slug: string): Promise<VehicleDetail | null> {
  const res = await fetch(getApiUrl(`/api/v1/web/vehicles/${slug}`), {
    headers: { Accept: "application/json" },
  });
  const json = await res.json();
  if (!json?.success || !json?.data) return null;
  const v = Array.isArray(json.data) ? json.data[0] : json.data;

  const firstVariant = v.variants && v.variants.length > 0 ? v.variants[0] : null;
  const specs = firstVariant?.specifications || v.specifications || [];
  const specMap = Object.fromEntries(specs.map((s: VehicleSpec) => [s.key, s.value]));

  const images = (firstVariant?.images && firstVariant.images.length > 0) ? firstVariant.images : (v.images || []);
  const sortedImages = [...images].sort((a: VehicleImage, b: VehicleImage) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
  const firstImage = sortedImages[0]?.image_url || v.image_url;

  return {
    id: v.id,
    vehicle_model_id: v.id,
    vehicle_variant_id: firstVariant?.id,
    name: v.name,
    variant_name: firstVariant?.name || "",
    slug: v.slug,
    vehicle_type: v.vehicle_type || "car",
    body_type: v.body_type || specMap.body_type || "",
    segment: firstVariant?.segment || specMap.segment || v.segment || "",
    fuel_type: firstVariant?.fuel_type || specMap.fuel_type || v.fuel_type || "",
    transmission: firstVariant?.transmission || specMap.transmission || v.transmission || "",
    seating_capacity: firstVariant?.seating_capacity ?? (parseInt(specMap.seating_capacity, 10) || 0),
    image_url: firstImage || "",
    brand: v.brand,
    model: v.model,
    about: v.about,
    meta_title: v.meta_title,
    meta_description: v.meta_description,
    meta_keywords: v.meta_keywords,
    specifications: specs,
    images: sortedImages.length > 0 ? sortedImages : (v.image_url ? [{ id: 0, image_url: v.image_url, is_primary: true }] : []),
  };
}

let citiesPromise: Promise<CityOption[]> | null = null;
const vehiclePromises = new Map<string, Promise<Vehicle[]>>();

/** Fetch vehicles with search params */
export async function fetchVehicles(params: {
  vehicle_type: "car" | "bike";
  booking_type: string;
  city_id?: number;
  pickup_at: string;
  return_at?: string;
  trip_type?: string;
}): Promise<Vehicle[]> {
  const cacheKey = `${params.vehicle_type}:${params.booking_type}:${params.city_id || ""}:${params.pickup_at}:${params.return_at || ""}:${params.trip_type || ""}`;

  if (vehiclePromises.has(cacheKey)) {
    return vehiclePromises.get(cacheKey)!;
  }

  const promise = (async () => {
    try {
      const searchParams = new URLSearchParams({
        vehicle_type: params.vehicle_type,
        booking_type: params.booking_type,
        pickup_at: params.pickup_at,
      });
      if (params.city_id) {
        searchParams.set("city_id", String(params.city_id));
      }
      if (params.return_at) {
        searchParams.set("return_at", params.return_at);
      }
      if (params.trip_type) {
        searchParams.set("trip_type", params.trip_type);
      }
      const res = await fetch(getApiUrl(`/api/v1/web/vehicles?${searchParams}`), {
        headers: { Accept: "application/json" },
      });
      const json: VehiclesResponse = await res.json();
      if (!json.success || !Array.isArray(json.data)) return [];
      return json.data;
    } catch {
      return [];
    } finally {
      setTimeout(() => {
        vehiclePromises.delete(cacheKey);
      }, 1000);
    }
  })();

  vehiclePromises.set(cacheKey, promise);
  return promise;
}

/** Fetch locations for a city */
export async function fetchLocations(cityId: number): Promise<LocationOption[]> {
  const res = await fetch(getApiUrl(`/api/v1/web/locations?city_id=${cityId}`), {
    headers: { Accept: "application/json" },
  });
  const json = await res.json();
  if (!json?.success || !Array.isArray(json?.data)) return [];
  return json.data
    .filter((l: { status?: string }) => l.status === "active")
    .map((l: { id: number; city_id: number; name: string; address: string; pincode: string; distance?: string | number | null; charge?: string | number | null }) => ({
      id: l.id,
      city_id: l.city_id,
      name: l.name,
      address: l.address,
      pincode: l.pincode,
      distance: l.distance,
      charge: l.charge,
    }));
}

/** Fetch cities with id and name */
export async function fetchCities(): Promise<CityOption[]> {
  if (citiesPromise) {
    return citiesPromise;
  }

  citiesPromise = (async () => {
    try {
      const res = await fetch(getApiUrl("/api/v1/web/cities"), {
        headers: { Accept: "application/json" },
      });
      const json = await res.json();
      if (!json?.success || !Array.isArray(json?.data)) return [];
      return json.data
        .filter((c: { status?: string }) => c.status === "active")
        .map((c: { id: number; name: string }) => ({ id: c.id, name: c.name }));
    } catch {
      return [];
    } finally {
      setTimeout(() => {
        citiesPromise = null;
      }, 1000);
    }
  })();

  return citiesPromise;
}

export interface CreateBookingDraftPayload {
  vehicle_variant_id: number;
  vehicle_rate_id?: number;
  selected_package_id?: number;
  chauffeur_rider_package_id?: number;
  city_id?: number | null;
  pickup_location_id?: number | null;
  dropoff_location_id?: number | null;
  sight_seeing_location_id?: number;
  pickup_address?: string;
  pickup_lat?: number | null;
  pickup_latitude?: number | null;
  pickup_lng?: number | null;
  pickup_longitude?: number | null;
  dropoff_address?: string;
  dropoff_lat?: number | null;
  dropoff_latitude?: number | null;
  dropoff_lng?: number | null;
  dropoff_longitude?: number | null;
  sightseeing_stops?: any;
  route_distance_km?: number | null;
  operational_distance_km?: number | null;
  estimated_duration_hours?: number | null;
  pickup_at: string;
  return_at: string;
  booking_type: string;
  vehicle_type: string;
  trip_type?: string;
  driver_pickup?: number;
  driver_drop?: number;
  pickup_charge?: number;
  dropoff_charge?: number;
  chauffeur_rider_pickup_address?: string;
  chauffeur_rider_pickup_latitude?: number | null;
  chauffeur_rider_pickup_longitude?: number | null;
  chauffeur_rider_dropoff_address?: string;
  chauffeur_rider_dropoff_latitude?: number | null;
  chauffeur_rider_dropoff_longitude?: number | null;
  chauffeur_rider_sightseeing_stops?: any;
  chauffeur_rider_route_distance_km?: number | null;
  chauffeur_rider_operational_distance_km?: number | null;
  chauffeur_rider_estimated_duration_hours?: number | null;
}

export interface BookingStop {
  id: number;
  booking_id: number;
  sequence_no: number;
  stop_type: string;
  location_name: string;
  address: string;
  latitude: number;
  longitude: number;
  google_place_id?: string | null;
  estimated_distance_from_prev_km?: number | null;
  estimated_duration_from_prev_mins?: number | null;
  status: string; // 'pending' | 'en_route' | 'arrived' | 'departed' | 'skipped'
  arrived_at?: string | null;
  departed_at?: string | null;
  waiting_duration_minutes?: number | null;
  driver_arrival_lat?: number | null;
  driver_arrival_lng?: number | null;
  odometer_at_arrival?: number | null;
  notes?: string | null;
}

export interface RouteSummary {
  pickup_address?: string | null;
  pickup_lat?: number | null;
  pickup_lng?: number | null;
  dropoff_address?: string | null;
  dropoff_lat?: number | null;
  dropoff_lng?: number | null;
  sightseeing_stops?: any[] | null;
  route_distance_km?: number | null;
  operational_distance_km?: number | null;
  estimated_duration_hours?: number | null;
  formatted_distance?: string | null;
  estimated_travel_time?: string | null;
  route_summary?: string | null;
  hub_name?: string | null;
}

export interface BookingDraftSummary {
  booking_uuid: string;
  status: string;
  vehicle: Vehicle;
  rate_package: {
    id?: number;
    km_limit?: number | null;
    hour_limit?: number | null;
    extra_time_charge?: string | null;
    price: number;
    extra_km_charge: string;
  };
  route?: RouteSummary;
  trip: {
    city_id?: number | null;
    city_name?: string | null;
    vehicle_type: string;
    booking_type: string;
    trip_type?: string;
    days: number;
    pickup_at: string;
    return_at: string;
    pickup_location: LocationOption | null;
    dropoff_location: LocationOption | null;
    sight_seeing_location?: LocationOption | null;
  };
  pricing: {
    daily_rate: number;
    days: number;
    base_fare?: number;
    package_price?: number | null;
    package_km_limit?: number | null;
    allowed_km?: number | null;
    route_km?: number | null;
    extra_km?: number;
    extra_km_rate?: number;
    extra_km_charge?: number;
    extra_time?: number;
    extra_hours?: number;
    extra_time_rate?: number;
    extra_time_charge?: number;
    convenience_fee?: number;
    rental_cost: number;
    driver_pickup?: number;
    driver_drop?: number;
    pickup_charge?: number;
    dropoff_charge?: number;
    coupon_id?: number | null;
    coupon_code?: string | null;
    coupon_discount?: number;
    discount_amount?: number;
    raw_subtotal?: number;
    service_charge_percent?: number;
    service_charge?: number;
    gst_percent: number;
    gst: number;
    subtotal: number;
    security_deposit: number;
    premium_support?: number;
    premium_support_fee?: number;
    is_premium_support_selected?: boolean;
    terms_version?: string | null;
    cancellation_refund_version?: string | null;
    privacy_policy_version?: string | null;
    rental_agreement_version?: string | null;
    policies_accepted_at?: string | null;
    total_payable: number;
  };
}

export async function createBookingDraft(payload: CreateBookingDraftPayload): Promise<string> {
  const url = getApiUrl("/api/v1/web/booking-drafts");
  const authHeaders = getAuthHeaders();
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders,
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });
  } catch {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders,
      },
      body: JSON.stringify(payload),
    });
  }
  const json = await res.json();
  if (!json?.success || !json?.data?.booking_uuid) {
    throw new Error(json?.message || "Failed to create booking draft");
  }
  return json.data.booking_uuid;
}

export async function fetchBookingDraft(uuid: string): Promise<BookingDraftSummary> {
  const url = getApiUrl(`/api/v1/web/booking-drafts/${uuid}`);
  const authHeaders = getAuthHeaders();
  let res: Response;
  try {
    res = await fetch(url, {
      headers: {
        Accept: "application/json",
        ...authHeaders,
      },
      credentials: "include",
    });
  } catch {
    res = await fetch(url, {
      headers: {
        Accept: "application/json",
        ...authHeaders,
      },
    });
  }
  const json = await res.json();
  if (!json?.success || !json?.data) {
    throw new Error(json?.message || "Booking draft not found");
  }
  return json.data;
}

export interface ConfirmedBookingDetails {
  id: number;
  booking_code: string;
  status: string;
  payment_status: string;
  total_amount: number;
  vehicle_name: string;
  pickup_at: string;
  return_at: string;
  coupon_code?: string | null;
  discount_amount?: number;
}

export async function confirmBookingDraft(uuid: string): Promise<ConfirmedBookingDetails> {
  const url = getApiUrl(`/api/v1/web/booking-drafts/${uuid}/confirm`);
  const authHeaders = getAuthHeaders();
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders,
      },
      credentials: "include",
    });
  } catch {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders,
      },
    });
  }
  const json = await res.json();
  if (!json?.success || !json?.data) {
    throw new Error(json?.message || "Failed to confirm booking");
  }
  return json.data;
}

export interface DurationCalculationResult {
  pickup_at: string;
  return_at: string;
  base_days: number;
  pickup_adjustment: number;
  return_adjustment: number;
  duration_days: number;
  formatted_duration: string;
}

/** Pure local calculation matching central single source of truth rules */
export function calculateRentalDurationLocal(pickupAt: string, returnAt: string): DurationCalculationResult {
  if (!pickupAt || !returnAt) {
    return {
      pickup_at: pickupAt || "",
      return_at: returnAt || "",
      base_days: 0,
      pickup_adjustment: 0,
      return_adjustment: 0,
      duration_days: 1,
      formatted_duration: "1 Day",
    };
  }

  const p = new Date(pickupAt.includes(" ") ? pickupAt.replace(" ", "T") : pickupAt);
  const r = new Date(returnAt.includes(" ") ? returnAt.replace(" ", "T") : returnAt);

  if (isNaN(p.getTime()) || isNaN(r.getTime())) {
    return {
      pickup_at: pickupAt,
      return_at: returnAt,
      base_days: 0,
      pickup_adjustment: 0,
      return_adjustment: 0,
      duration_days: 1,
      formatted_duration: "1 Day",
    };
  }

  const pickupStartOfDay = new Date(p.getFullYear(), p.getMonth(), p.getDate());
  const returnStartOfDay = new Date(r.getFullYear(), r.getMonth(), r.getDate());
  const diffMs = returnStartOfDay.getTime() - pickupStartOfDay.getTime();
  const base_days = Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));

  // Pickup before 8:00 AM => +1
  const pickup_adjustment = p.getHours() < 8 ? 1 : 0;

  // Return after 7:00 AM => +1
  const isAfter7AM = r.getHours() > 7 || (r.getHours() === 7 && (r.getMinutes() > 0 || r.getSeconds() > 0));
  const return_adjustment = isAfter7AM ? 1 : 0;

  const rawDays = base_days + pickup_adjustment + return_adjustment;
  const duration_days = Math.max(1, rawDays);
  const formatted_duration = `${duration_days} ${duration_days === 1 ? "Day" : "Days"}`;

  return {
    pickup_at: pickupAt,
    return_at: returnAt,
    base_days,
    pickup_adjustment,
    return_adjustment,
    duration_days,
    formatted_duration,
  };
}

/** Fetches rental duration from central backend single source of truth */
export async function fetchRentalDuration(pickupAt: string, returnAt: string): Promise<DurationCalculationResult> {
  const localFallback = calculateRentalDurationLocal(pickupAt, returnAt);
  if (!pickupAt || !returnAt) return localFallback;

  try {
    const url = getApiUrl(`/api/v1/web/calculate-duration`);
    const cleanPickup = pickupAt.includes("T") || pickupAt.includes("Z") ? toApiDateTime(pickupAt) : pickupAt;
    const cleanReturn = returnAt.includes("T") || returnAt.includes("Z") ? toApiDateTime(returnAt) : returnAt;

    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ pickup_at: cleanPickup, return_at: cleanReturn }),
    });

    if (!res.ok) return localFallback;
    const json = await res.json();
    if (json?.success && json?.data) {
      return json.data;
    }
    return localFallback;
  } catch {
    return localFallback;
  }
}

export async function applyCouponToDraft(uuid: string, code: string): Promise<BookingDraftSummary> {
  const url = getApiUrl(`/api/v1/web/booking-drafts/${uuid}/apply-coupon`);
  const authHeaders = getAuthHeaders();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ code }),
  });
  const json = await res.json();
  if (!json?.success || !json?.data) {
    const err = new Error(json?.message || "Failed to apply coupon");
    (err as any).errorCode = json?.error_code || "COUPON_ERROR";
    throw err;
  }
  return json.data;
}

export async function removeCouponFromDraft(uuid: string): Promise<BookingDraftSummary> {
  const url = getApiUrl(`/api/v1/web/booking-drafts/${uuid}/remove-coupon`);
  const authHeaders = getAuthHeaders();
  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders,
    },
  });
  const json = await res.json();
  if (!json?.success || !json?.data) {
    throw new Error(json?.message || "Failed to remove coupon");
  }
  return json.data;
}

export async function updateDraftOptions(
  uuid: string,
  isPremiumSupportSelected: boolean
): Promise<BookingDraftSummary> {
  const url = getApiUrl(`/api/v1/web/booking-drafts/${uuid}/update-options`);
  const authHeaders = getAuthHeaders();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ is_premium_support_selected: isPremiumSupportSelected }),
  });
  const json = await res.json();
  if (!json?.success || !json?.data) {
    throw new Error(json?.message || "Failed to update draft options");
  }
  return json.data;
}




